import * as css from 'css/exports.module.scss';
import './TeamInvites.scss';
import { CheckCircleOutlined } from '@ant-design/icons';
import difference from 'lodash/difference';
import { When } from 'react-if';
import { Button, Col, Form, Row, Space } from 'antd';
import { Email, FirstName, LastName } from '@ui/form-items';
import { NOTIFICATIONS_COPY, ONBOARDING_SYNC_COPY } from '@sonar-software/sonar-ts-constants';
import React, { useCallback, useEffect, useState } from 'react';
import { useInvite, usePrevious } from '@hooks';
const defaultRecords = [{ index: 0 }, { index: 1 }];
function hasRequiredValues(invite) {
    return Boolean(invite.firstName) && Boolean(invite.lastName) && Boolean(invite.email);
}
function hasValidValues(invite, form) {
    const { index } = invite;
    const isFirstNameValid = form.getFieldError(`firstName-${index}`).length === 0;
    const isLastNameValid = form.getFieldError(`lastName-${index}`).length === 0;
    const isEmailValid = form.getFieldError(`email-${index}`).length === 0;
    return isFirstNameValid && isLastNameValid && isEmailValid;
}
function getInvitesToSend(invites, form) {
    return invites.filter((invite) => !invite.processed && hasRequiredValues(invite) && hasValidValues(invite, form));
}
function rebuildInvites(currentInvites, name, value) {
    const nameParts = name.split('-');
    const inputIndex = Number(nameParts[1]);
    const targetInvite = currentInvites[inputIndex];
    const newInvites = [
        ...currentInvites.slice(0, inputIndex),
        Object.assign(Object.assign({}, targetInvite), { [nameParts[0]]: value.trim() }),
        ...currentInvites.slice(inputIndex + 1)
    ];
    if (inputIndex === currentInvites.length - 1) {
        newInvites.push({ index: currentInvites.length });
    }
    return newInvites;
}
function rebuildInvitesWithProcessed(currentInvites, validInvites) {
    return currentInvites.map((invite) => {
        if (validInvites.some((validInvite) => validInvite.index === invite.index)) {
            return Object.assign(Object.assign({}, invite), { processed: true });
        }
        return invite;
    });
}
function displayInvalidMessages(form, invalidInvites) {
    invalidInvites.forEach((invite) => {
        form.setFields([
            {
                errors: [NOTIFICATIONS_COPY.FAILED_USER_INVITE],
                name: `email-${invite.index}`
            }
        ]);
    });
}
export const TeamInvites = () => {
    const [inviteForm] = Form.useForm();
    const [invites, setInvites] = useState(defaultRecords);
    const { finished, invite, loading, result } = useInvite();
    const previousFinished = usePrevious(finished);
    useEffect(() => {
        if (!result) {
            return;
        }
        if (finished && previousFinished) {
            return;
        }
        const sentInvites = getInvitesToSend(invites, inviteForm);
        const invalidInvites = sentInvites.filter((currentInvite) => result.invalidUsers.some((invalidUser) => invalidUser.email === currentInvite.email));
        const validInvites = difference(sentInvites, invalidInvites);
        displayInvalidMessages(inviteForm, invalidInvites);
        setInvites(rebuildInvitesWithProcessed(invites, validInvites));
    }, [finished, inviteForm, invites, previousFinished, result]);
    const handleSubmit = useCallback(() => {
        const invitesToSend = getInvitesToSend(invites, inviteForm);
        if (invitesToSend.length) {
            invite({
                users: invitesToSend
            });
        }
    }, [inviteForm, invite, invites]);
    const handelValuesChange = useCallback((updatedValues) => {
        Object.keys(updatedValues).forEach((key) => {
            setInvites((currentInvites) => { var _a; return rebuildInvites(currentInvites, key, (_a = updatedValues[key]) === null || _a === void 0 ? void 0 : _a.trim()); });
        });
    }, []);
    return (React.createElement(Form, { className: 'team-invites', form: inviteForm, onValuesChange: handelValuesChange },
        invites.map((currentInvite) => {
            const { index } = currentInvite;
            const key = `invite-${index}`;
            const firstName = `firstName-${index}`;
            const lastName = `lastName-${index}`;
            const email = `email-${index}`;
            const { processed } = currentInvite;
            return (React.createElement(Row, { gutter: Number(css.size8), key: key },
                React.createElement(Col, { span: 1 },
                    React.createElement(When, { condition: processed },
                        React.createElement(CheckCircleOutlined, { className: 'processed-indicator' }))),
                React.createElement(Col, { span: 6 },
                    React.createElement(FirstName, { disabled: processed || loading, name: firstName, size: 'large' })),
                React.createElement(Col, { span: 6 },
                    React.createElement(LastName, { disabled: processed || loading, name: lastName, size: 'large' })),
                React.createElement(Col, { span: 9 },
                    React.createElement(Email, { disabled: processed || loading, name: email, size: 'large' }))));
        }),
        React.createElement(Row, { justify: 'center' },
            React.createElement(Space, { size: Number(css.size24) },
                React.createElement(Button, { disabled: loading, id: 'onboarding-invite-button', onClick: handleSubmit, size: 'large', type: 'primary' }, ONBOARDING_SYNC_COPY.SEND_INVITES))),
        React.createElement(When, { condition: finished },
            React.createElement("p", null, ONBOARDING_SYNC_COPY.INVITES_SENT))));
};
